import { cn } from '@/lib/utils';

interface LogoProps {
  className?: string;
  /** Size variant for different contexts */
  size?: 'sm' | 'md' | 'lg' | 'responsive';
}

/**
 * YELV Logo component - renders the SVG inline for theme compatibility.
 * Uses currentColor so it inherits text color from parent for light/dark mode.
 * 
 * Size variants:
 * - sm: 16px height (sidebar collapsed, footer)
 * - md: 20px height (sidebar, headers)
 * - lg: 28px height (marketing, hero)
 * - responsive: clamp(20px, 4vw, 32px) - scales with viewport
 */
export function Logo({ className, size = 'md' }: LogoProps) {
  const sizeStyles = {
    sm: 'h-5', // 20px
    md: 'h-7', // 28px - prominent sidebar brand size
    lg: 'h-8', // 32px
    responsive: '', // handled via inline style
  };

  return (
    <span 
      className={cn(
        "inline-flex items-center leading-none",
        // Isolate from parent text styles
        "text-[0px]",
      )}
      role="img"
      aria-label="YELV"
    >
      <svg
        viewBox="0 0 504 288"
        fill="currentColor"
        aria-hidden="true"
        className={cn(
          // Ensure the SVG doesn't inherit text sizing
          "block flex-shrink-0",
          sizeStyles[size],
          className
        )}
        style={size === 'responsive' ? {
          height: 'clamp(1.5rem, 3vw + 0.75rem, 2.5rem)', // 24px min, ~40px max
          width: 'auto',
        } : undefined}
      >
        <path d="M279.21,164.12c-.08.85-.03,1.51-.22,2.07-4.7,13.7-13.54,23.58-27.1,28.95-9.06,3.59-18.41,4.38-28.1,3.24-6.77-.79-13.18-2.43-19.13-5.66-9.75-5.28-16.89-13.01-21.2-23.29-4.51-10.76-5.35-21.91-4.01-33.4.83-7.14,2.81-13.83,6.3-20.12,6.32-11.42,15.82-18.94,28.24-22.62,6.32-1.87,12.87-2.76,19.51-2.39,13.94.78,26.18,5.32,35.66,16.06,5.43,6.15,8.66,13.35,10.46,21.28,1.64,7.21,2.11,14.5,1.65,22.49h-84.92c1.01,10.28,4.09,19.15,12.02,25.8,5.53,4.64,11.89,7.25,19.05,7.56,5.13.23,10.27.42,15.3-1.47,8.13-3.05,14.3-8.04,17.68-16.19.3-.72.65-1.41,1.08-2.32h17.76ZM263.11,136.24c0-3.27-.85-6.49-1.8-9.64-1.73-5.71-4.96-10.57-9.6-14.26-8.57-6.81-18.66-8.26-28.96-6.2-12.24,2.45-20.12,10.5-24.15,22.18-.88,2.55-1.29,4.44-1.54,8.13l66.06-.21Z" />
        <path d="M97.38,236.01c1.29-3.3,2.49-6.49,3.78-9.63,3.87-9.41,7.68-18.85,11.75-28.18,1.03-2.36.44-4.27-.37-6.28-2.8-6.93-5.68-13.84-8.52-20.75-4.69-11.44-9.36-22.89-14.08-34.31-5.02-12.16-10.08-24.29-15.11-36.44-.93-2.26-1.79-4.67-2.83-7.3h18.92c10.78,27.14,21.55,54.39,32.55,82.1,10.95-27.64,21.66-54.89,32.44-82.1h18.1c-.79,3.43-2.23,6.73-3.51,9.86-4.16,10.13-8.36,20.25-12.54,30.38-3.35,8.1-6.69,16.21-10.04,24.31-6.41,15.48-12.86,30.95-19.25,46.45-3.83,9.29-7.55,18.62-11.34,27.92-.53,1.31-1.21,2.55-1.9,4h-18.05Z" />
        <path d="M432,93.1c-14.2,34.84-28.14,69.1-42.11,103.36h-18.36c-14.06-34.32-28.37-69.2-42.37-103.36h19.05c10.91,27.26,21.59,54.81,32.67,82.89,11-27.9,21.79-55.31,32.66-82.89h18.45Z" />
        <path d="M299.39,196.44c0-48.09,0-96.53,0-144.46h16.99v144.46h-16.99Z" />
      </svg>
    </span>
  );
}
